import { FILTER_OPERATIONS } from 'components/Grids/DataGrid/Filters/utils/types';
import { FILTRATION_TYPES } from 'constants/common';
import {
  IBaseGist,
  IFilterSetting,
  IGist,
  IOrdersType,
  IPageType,
  IRedirectLinkResponse,
  ISortType,
  TObject,
  TSortDirection,
} from 'utils/types';

type IFilterData = TObject & {
  listOperations?: string[];
};

type ISortData = TObject & {
  orders?: TObject[];
};

const normalizeOrder = (order: TObject): IOrdersType => {
  return {
    ascending: !!order?.ascending,
    descending: !!order?.descending,
    direction: (order?.direction as TSortDirection) || 'ASC',
    ignoreCase: !!order?.ignoreCase,
    nullHandling: (order?.nullHandling as string) || '',
    property: (order?.property as string) || '',
  };
};

const normalizeFilterVariant = (data: IFilterData): IFilterSetting => {
  return {
    defaultSorting: !!data?.defaultSorting,
    groupEdit: !!data.groupEdit,
    isRequestFilter: !!data.searchValues,
    listOperations:
      data?.listOperations?.map((operation) => (operation as FILTER_OPERATIONS) || '') ||
      [],
    maxRangeDays: (data?.maxRangeDays as number) || undefined,
    multiSelect: !!data.multiSelect,
    searchNullable: !!data?.searchNullable,
    sortable: !!data?.sortable,
    type: (data?.type as FILTRATION_TYPES) || '',
  };
};

type IFiltersData = Record<string, IFilterData>;
type ISortingData = ISortData & TObject;

const normalizeFilters = <T = string>(
  data: IFiltersData,
): Record<keyof T, IFilterSetting> => {
  const normalizedFilters = Object.entries(data).map(([key, value]) => [
    key,
    normalizeFilterVariant(value),
  ]);

  return Object.fromEntries(normalizedFilters);
};

const normalizeSort = (data: ISortData): ISortType => {
  return {
    empty: !!data?.empty,
    orders: data?.orders?.map(normalizeOrder) || [],
    sorted: !!data?.sorted,
    unsorted: !!data?.unsorted,
  };
};

export type TServerData<T> = TObject & {
  content: T[];
  filters: IFiltersData;
  sort: ISortingData;
};

export function normalizeBaseGist(data: TObject): IBaseGist {
  return {
    id: (data.id as number) || 0,
  };
}

export function normalizeGist(data: TObject): IGist {
  return {
    id: (data.id as number) || 0,
    name: (data.name as string) || '',
  };
}

export function normalizePageData<T, U>(
  data: TServerData<T>,
  normalizer: (data: T, index: number, array: T[]) => U,
  searchString?: string,
): IPageType<U> {
  return {
    currentPage: (data.number as number) || 0,
    data: data.content?.map(normalizer) || [],
    empty: !!data?.empty,
    filters: normalizeFilters<U>(data?.filters || {}),
    first: !!data?.first,
    hasResults: !!Object.keys(data?.filters || {}).length,
    last: !!data?.last,
    numberOfElements: (data.numberOfElements as number) || 0,
    searchString,
    size: (data.size as number) || 0,
    sort: normalizeSort(data?.sort || {}),
    totalElements: (data.totalElements as number) || 0,
    totalPages: (data.totalPages as number) || 0,
  };
}

export const normalizeNaming = (text: string) => {
  const modifiedText = text.replaceAll('_', ' ').toLowerCase();
  return modifiedText.charAt(0).toUpperCase() + modifiedText.slice(1);
};

export const normalizeBooleanFields = <T>(data: TObject): T => {
  return Object.fromEntries<boolean>(
    Object.entries<unknown>(data).map(([key, value]) => [key, !!value]),
  ) as T;
};

export const normalizeRedirectLink = (data: TObject): IRedirectLinkResponse => {
  return {
    redirectLink: (data?.redirectLink as string) || '',
  };
};
